import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileDown, Printer, Loader2, Search, CheckSquare, Square, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_APPS = 10;

const DOC_OPTIONS = [
  { key: "borrower", label: "Borrower Copy (borrower eyes only)" },
  { key: "lender", label: "Counterparty / Lender Package" },
  { key: "draft", label: "Draft (Private Negotiable Instrument)" },
  { key: "both", label: "Borrower Copy + Lender Package" },
];

export default function BulkDocsPanel({ loans = [] }) {
  const token = localStorage.getItem("th_admin_token") || "";
  const [doc, setDoc] = useState("borrower");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (loans || [])
      .map((l) => ({
        id: l.id,
        name: (l.binder?.borrowerName || l.legal_name || l.borrower_name || l.id || "").toString(),
        status: l.status || "—",
      }))
      .filter((r) => !q || r.name.toLowerCase().includes(q) || r.status.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [loans, query]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size >= MAX_APPS) { toast.error(`Select at most ${MAX_APPS} at a time`); return prev; }
      else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => {
    if (allVisibleSelected) setSelected(new Set());
    else setSelected(new Set(rows.slice(0, MAX_APPS).map((r) => r.id)));
  };

  const fetchPdf = async (ids) => {
    const res = await axios.post(`${API}/admin/documents/bulk`, { app_ids: ids, doc },
      { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
    return window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  };
  const errMsg = async (e) => {
    try { return JSON.parse(await e.response.data.text()).detail; } catch { return "Request failed"; }
  };

  const download = async (ids) => {
    if (!ids.length) { toast.error("Select at least one borrower"); return; }
    setBusy(true);
    try {
      const url = await fetchPdf(ids);
      const a = document.createElement("a");
      a.href = url;
      a.download = ids.length > 1 ? `seutrade_documents_${ids.length}_borrowers.pdf` : "seutrade_documents.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ids.length} borrower${ids.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error(await errMsg(e)); } finally { setBusy(false); }
  };
  const printDocs = async (ids) => {
    if (!ids.length) { toast.error("Select at least one borrower"); return; }
    setBusy(true);
    try {
      const url = await fetchPdf(ids);
      const w = window.open(url, "_blank");
      if (w) w.addEventListener("load", () => { try { w.print(); } catch { /* noop */ } });
      toast.success("Opening print view");
    } catch (e) { toast.error(await errMsg(e)); } finally { setBusy(false); }
  };

  const selectedIds = Array.from(selected);

  return (
    <div className="space-y-5" data-testid="bulk-docs-panel">
      <div>
        <h2 className="text-[20px] font-semibold text-[#ECECE4] flex items-center gap-2">
          <FileText size={18} className="text-[#E4C97A]" /> Document Center
        </h2>
        <p className="text-[12px] text-[#8A8A80] mt-1">
          Generate, download and print each borrower's documents — one at a time or in bulk (up to {MAX_APPS} at once, merged into a single PDF).
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A80]">Documents</span>
          <select value={doc} onChange={(e) => setDoc(e.target.value)} data-testid="bulk-docs-doc-select"
            className="bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-2 py-1.5 text-[12px] text-[#ECECE4]">
            {DOC_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[180px]">
          <Search size={13} className="text-[#8A8A80]" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search borrowers…"
            data-testid="bulk-docs-search"
            className="flex-1 bg-transparent text-[12px] text-[#ECECE4] outline-none placeholder-[#5A5A52]" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[#8A8A80]" data-testid="bulk-docs-selected-count">{selected.size} selected</span>
          <button onClick={() => printDocs(selectedIds)} disabled={busy || !selected.size}
            data-testid="bulk-docs-print-selected"
            className="flex items-center gap-1.5 text-[12px] rounded-md px-3 py-1.5 border border-[#6C8CD5]/50 text-[#6C8CD5] hover:bg-[#6C8CD5]/10 disabled:opacity-40">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />} Print selected
          </button>
          <button onClick={() => download(selectedIds)} disabled={busy || !selected.size}
            data-testid="bulk-docs-download-selected"
            className="flex items-center gap-1.5 text-[12px] font-semibold rounded-md px-3 py-1.5 bg-[#E4C97A] text-[#0A0A0A] hover:bg-[#f0d98a] disabled:opacity-40">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Download selected
          </button>
        </div>
      </div>

      <div className="border border-[#2A2A22] rounded-md overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-[#12120C] border-b border-[#2A2A22]">
          <button onClick={toggleAll} data-testid="bulk-docs-select-all" className="text-[#8A8A80] hover:text-[#ECECE4]">
            {allVisibleSelected ? <CheckSquare size={15} /> : <Square size={15} />}
          </button>
          <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A80]">Borrower</span>
          <span className="ml-auto text-[10px] uppercase tracking-[0.12em] text-[#8A8A80]">Action</span>
        </div>
        <div className="divide-y divide-[#1F1F18] max-h-[520px] overflow-y-auto">
          {rows.length === 0 && (
            <div className="px-3 py-6 text-center text-[12px] text-[#8A8A80]" data-testid="bulk-docs-empty">No borrowers found.</div>
          )}
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#0F0F0A]" data-testid={`bulk-docs-row-${r.id}`}>
              <button onClick={() => toggle(r.id)} data-testid={`bulk-docs-check-${r.id}`}
                className="text-[#8A8A80] hover:text-[#E4C97A]">
                {selected.has(r.id) ? <CheckSquare size={15} className="text-[#E4C97A]" /> : <Square size={15} />}
              </button>
              <div className="min-w-0">
                <div className="text-[13px] text-[#ECECE4] truncate">{r.name}</div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-[#8A8A80]">{r.status}</div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => download([r.id])} disabled={busy} data-testid={`bulk-docs-download-${r.id}`}
                  className="flex items-center gap-1 text-[11px] text-[#7fe7c2] hover:underline disabled:opacity-40">
                  <FileDown size={12} /> Download
                </button>
                <button onClick={() => printDocs([r.id])} disabled={busy} data-testid={`bulk-docs-print-${r.id}`}
                  className="flex items-center gap-1 text-[11px] text-[#6C8CD5] hover:underline disabled:opacity-40">
                  <Printer size={12} /> Print
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
